<template>
	<sg-modal v-model="isOpen" title="Add Plantings" :backdrop-dismiss="false">
		<template #header>
			<ion-title>Add Plantings</ion-title>
			<ion-buttons slot="start">
				<ion-button fill="clear" color="danger" @click="clearQueue">
					Reset
				</ion-button>
			</ion-buttons>
			<ion-buttons slot="end">
				<ion-button @click="closeGlobalModal">
					<ion-icon :icon="closeOutline" />
				</ion-button>
			</ion-buttons>
		</template>

		<div class="ion-padding">
			<!-- Action Buttons -->
			<div class="mb-4 grid grid-cols-2 gap-2">
				<sg-button expand="block" fill="outline" @click="openAddModal">
					<ion-icon slot="start" :icon="addOutline" />
					Add Planting
				</sg-button>
				<sg-button
					expand="block"
					fill="outline"
					:loading="isParsingFile"
					:disabled="isParsingFile"
					@click="handleImport"
				>
					<ion-icon slot="start" :icon="cloudUploadOutline" />
					Import from Excel/CSV
				</sg-button>
			</div>

			<!-- Queue List -->
			<div v-if="queue.length > 0" class="flex flex-col gap-3">
				<PlantingFormDataCard
					v-for="(item, index) in queue"
					:key="index"
					:planting-form-data="item"
					:error-message="queueErrors.get(index)"
					@edit="editQueueItem(index)"
					@remove="removeQueueItem(index)"
				/>
			</div>

			<!-- Empty State -->
			<div
				v-else
				class="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500"
			>
				<p class="mb-2">No plantings added yet.</p>
				<p class="mb-4 text-sm">
					Add plantings manually or import from Excel/CSV file.
				</p>
				<button
					class="text-sm text-blue-600 underline hover:text-blue-800"
					@click="downloadTemplate"
				>
					Download import template (Excel)
				</button>
			</div>
		</div>

		<template #footer>
			<div class="flex flex-row gap-2">
				<sg-button
					color="medium"
					class="flex-1"
					:disabled="isSaving"
					@click="closeGlobalModal"
				>
					Close
				</sg-button>
				<sg-button
					class="flex-1"
					:loading="isSaving"
					:disabled="!canSaveAll || isSaving"
					@click="saveAll"
				>
					Save All ({{ queue.length }})
				</sg-button>
			</div>
		</template>
	</sg-modal>

	<!-- Add/Edit Planting Modal -->
	<AddEditPlantingModal ref="addEditModalRef" @save="handlePlantingAdd" />

	<!-- Hidden file input for import -->
	<input
		ref="fileInputRef"
		type="file"
		accept=".xlsx,.xls,.csv"
		class="hidden"
		@change="handleFileSelect"
	/>
</template>

<script setup lang="ts">
	import { ref, computed, watch } from "vue";
	import { IonButton, IonIcon, IonTitle, IonButtons } from "@ionic/vue";
	import {
		addOutline,
		closeOutline,
		cloudUploadOutline,
	} from "ionicons/icons";
	import { useRoute } from "vue-router";
	import { useModalNavigation } from "@/composables/useModalNavigation";
	import SgModal from "@/components/ui/modals/SgModal.vue";
	import AddEditPlantingModal from "@/components/ui/modals/AddEditPlantingModal.vue";
	import PlantingFormDataCard from "@/components/ui/cards/PlantingFormDataCard.vue";
	import SgButton from "@/components/ui/SgButton.vue";
	import type { PlantingFormData } from "@/validation/planting.schema";
	import {
		useCreateBatchPlantings,
		type CreatePlantingsResponse,
	} from "seedgreen-shared/queries/planting";
	import { sendToast } from "@/utils/sendToast";
	import { captureException } from "@sentry/vue";
	import { parsePlantingFile } from "@/utils/plantingImport";
	import { useCrops } from "seedgreen-shared/query/crops";
	import { useRanches } from "seedgreen-shared/queries/ranch";
	import { useLots } from "seedgreen-shared/queries/lot";
	import { BoundaryType } from "seedgreen-shared/dtos/PlantingDTO";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import { useMyCustomer } from "seedgreen-shared/queries/customer";

	const route = useRoute();
	const { closeGlobalModal } = useModalNavigation();
	const { crops } = useCrops();
	const { ranches } = useRanches();
	const { lots } = useLots();
	const { myUser } = useMyUser();
	const { myCustomer } = useMyCustomer();

	const isOpen = computed(() => route.query.modal === "batch-add-plantings");

	const queue = ref<PlantingFormData[]>([]);
	const queueErrors = ref<Map<number, string>>(new Map());
	const editingIndex = ref<number | undefined>(undefined);
	const isParsingFile = ref(false);

	const addEditModalRef = ref<InstanceType<
		typeof AddEditPlantingModal
	> | null>(null);

	const fileInputRef = ref<HTMLInputElement | null>(null);

	const { createPlantings, isPending: isSaving } = useCreateBatchPlantings();

	// Clear queue when customer changes
	watch(
		() => myCustomer.value?.id,
		(newCustomerId, oldCustomerId) => {
			// Only clear if customer actually changed (not on initial load)
			if (
				oldCustomerId !== undefined &&
				newCustomerId !== oldCustomerId
			) {
				clearQueue();
			}
		},
	);

	const canSaveAll = computed(() => {
		if (queue.value.length === 0) return false;
		// Can only save if ALL items have no parsing errors
		// Note: boundary warnings are OK, those are just warnings not blockers
		return queue.value.every((_, index) => !queueErrors.value.has(index));
	});

	function openAddModal() {
		editingIndex.value = undefined;
		addEditModalRef.value?.openModal();
	}

	function editQueueItem(index: number) {
		editingIndex.value = index;
		const item = queue.value[index];
		const hasErrors = queueErrors.value.has(index);
		// If item has errors, trigger validation immediately when modal opens
		addEditModalRef.value?.openModal(item, hasErrors);
	}

	function removeQueueItem(index: number) {
		queue.value.splice(index, 1);

		// Rebuild error map: shift down indices after the removed item
		const newErrors = new Map<number, string>();
		queueErrors.value.forEach((error, idx) => {
			if (idx < index) {
				// Keep same index if before removed item
				newErrors.set(idx, error);
			} else if (idx > index) {
				// Shift down by 1 if after removed item
				newErrors.set(idx - 1, error);
			}
		});
		queueErrors.value = newErrors;
	}

	function handlePlantingAdd(formData: PlantingFormData) {
		if (editingIndex.value !== undefined) {
			// Update existing item
			queue.value[editingIndex.value] = formData;
			// Clear error when user saves changes
			queueErrors.value.delete(editingIndex.value);
		} else {
			// Add new item
			queue.value.push(formData);
		}
		// Close the add/edit modal immediately (no API call needed)
		addEditModalRef.value?.closeModal();
	}

	async function downloadTemplate() {
		const res = await fetch("/templates/Planting_Import_Template.xlsx", {
			cache: "no-cache",
		});
		if (!res.ok) {
			sendToast(
				"Failed to download template. Please try again.",
				3000,
				"danger",
			);
			return;
		}
		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "Planting_Import_Template.xlsx";
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	}

	function handleImport() {
		fileInputRef.value?.click();
	}

	async function handleFileSelect(event: Event) {
		const target = event.target as HTMLInputElement;
		const file = target.files?.[0];

		if (!file) return;

		// Validate file type (accept attribute is not reliable on mobile)
		const allowedExtensions = [".xlsx", ".xls", ".csv"];
		const dotIndex = file.name.lastIndexOf(".");
		const fileExtension =
			dotIndex > 0 ? file.name.substring(dotIndex).toLowerCase() : "";

		if (!allowedExtensions.includes(fileExtension)) {
			sendToast(
				"Invalid file type. Please upload an Excel (.xlsx, .xls) or CSV file.",
				4000,
				"danger",
			);
			target.value = ""; // Clear input
			return;
		}

		// Validate required data is loaded
		if (!crops.value || !ranches.value || !lots.value) {
			sendToast(
				"Please wait for data to load before importing",
				3000,
				"warning",
			);
			target.value = ""; // Clear input to allow reselection
			return;
		}

		isParsingFile.value = true;

		try {
			// Parse file
			const result = await parsePlantingFile(
				file,
				crops.value,
				ranches.value,
				lots.value,
			);

			if (!result.success || result.rows.length === 0) {
				sendToast(
					result.errors[0] || "No valid rows found in file",
					4000,
					"danger",
				);
				target.value = ""; // Clear input to allow reselection
				return;
			}

			// Add all rows to queue (valid and invalid)
			const startIndex = queue.value.length;
			result.rows.forEach((row, idx) => {
				queue.value.push(row.data as PlantingFormData);

				// Add errors to map if row has errors
				if (row.errors.length > 0) {
					queueErrors.value.set(
						startIndex + idx,
						row.errors.join("; "),
					);
				}
			});

			// Show summary toast
			const validCount = result.rows.filter(
				(r) => r.errors.length === 0,
			).length;
			const errorCount = result.rows.length - validCount;

			if (errorCount === 0) {
				sendToast(
					`Imported ${validCount} planting${validCount > 1 ? "s" : ""}`,
					2000,
					"success",
				);
			} else if (validCount === 0) {
				sendToast(
					`Imported ${result.rows.length} row(s) with errors. Fix them below before saving.`,
					4000,
					"warning",
				);
			} else {
				sendToast(
					`Imported ${validCount} valid and ${errorCount} with errors. Fix errors below before saving.`,
					4000,
					"warning",
				);
			}
		} catch (error) {
			captureException(error);
			sendToast(
				"Failed to parse file. Please try again.",
				3000,
				"danger",
			);
		} finally {
			isParsingFile.value = false;

			// Reset file input so same file can be selected again
			if (fileInputRef.value) {
				fileInputRef.value.value = "";
			}
		}
	}

	async function saveAll() {
		if (!canSaveAll.value) return;

		try {
			const plantingsToSave = queue.value.map((planting) => ({
				...planting,
				customerId: myUser.value?.customerId,
				latestBoundaryDate:
					planting.latestBoundaryDate ||
					new Date().toISOString().split("T")[0],
				latestBoundaryType: BoundaryType.UserCreated,
				isVisible: true,
			}));

			// Send plantings to backend (wrapping is handled by the query)
			const result: CreatePlantingsResponse =
				await createPlantings(plantingsToSave);

			// Check for any errors
			if (result.errors && result.errors.length > 0) {
				// Map original indices to error messages
				const errorsByOriginalIndex = new Map<number, string>();
				result.errors.forEach((error) => {
					errorsByOriginalIndex.set(error.index, error.message);
				});

				// Build new queue with only failed items
				const failedItems: PlantingFormData[] = [];
				const newErrors = new Map<number, string>();

				queue.value.forEach((item, originalIndex) => {
					const errorMessage =
						errorsByOriginalIndex.get(originalIndex);
					if (errorMessage) {
						// This item failed - add it to new queue with error
						newErrors.set(failedItems.length, errorMessage);
						failedItems.push(item);
					}
					// Successful items are simply not added
				});

				// Replace queue and errors
				queue.value = failedItems;
				queueErrors.value = newErrors;

				// Show appropriate toast
				if (result.createdIds.length === 0) {
					// All failed
					sendToast(
						`Failed to create all plantings. Fix the errors below and try again.`,
						4000,
						"danger",
					);
				} else {
					// Partial success
					sendToast(
						`Created ${result.createdIds.length} of ${result.createdIds.length + result.errors.length} plantings. Fix the remaining errors and retry.`,
						5000,
						"warning",
					);
				}

				// Don't close modal - let user fix errors
			} else {
				// All succeeded
				sendToast(
					`Successfully created ${result.createdIds.length} planting${result.createdIds.length > 1 ? "s" : ""}`,
					2000,
					"success",
				);

				// Clear queue and close modal
				queue.value = [];
				queueErrors.value.clear();
				closeGlobalModal();
			}
		} catch (error) {
			captureException(error);
			sendToast(
				"Failed to create plantings. Please try again.",
				3000,
				"danger",
			);
		}
	}

	function clearQueue() {
		queue.value = [];
		queueErrors.value.clear();
	}
</script>
